import { Box, CircularProgress } from "@mui/material";
import { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import { Roles } from "@shared/Roles";
import { GeneralAlert } from "./components/alert/GeneralAlert";
import { ExcelButton } from "./components/excel-button/ExcelButton";
import { AdminLayout } from "./components/layout/AdminLayout";
import { WrapPrivateRoute } from "./components/private-route/WrapPrivateRoute";
import { AlertProvider } from "./contexts/AlertProvider";
import DialogProvider from "./contexts/DialogContext";
import { NotificationProvider } from "./contexts/NotificationProvider";
import { OrganizationProvider } from "./contexts/OrganizationProvider";
import { RequestProvider } from "./contexts/RequestProvider";
import { UserProvider } from "./contexts/UserProvider";
import { AdminHandlingProvider } from "./contexts/AdminHandlingProvider";
import { wrap } from "./helpers/wrap-and-provide";
import { Auth } from "./pages/auth/Auth";
import { GroupsCalendar } from "./pages/calender/GroupsCalendar";
import { EventCard } from "./pages/event-card/EventCard";
import { EventReports } from "./pages/event-reports/EventReports";
import FormsPage from "./pages/forms/FormsPage";
import { Hagai } from "./pages/hagai/Hagai";
import { NotFound } from "./pages/not-found/NotFound";
import { Notifications } from "./pages/notifications/Notifications";
import { Offline } from "./pages/offline/Offline";
import { OrganizationRegister } from "./pages/organization-register/OrganizationRegister";
import { OrganizationRequestFullContainer } from "./pages/organization-request/OrganizationRequestFullContainer";
import { PermissionPage } from "./pages/permissions/Permissions";
import SettingsPage from "./pages/settings/Settings";
import { Statistics } from "./pages/statistics/Statistics";
import { SuperAdmin } from "./pages/super-admin/SuperAdmin";
import AidRelatedTable from "./pages/tables/aid-related-table";
import AssociationRequestedTable from "./pages/tables/association-requested-table";
import VolunteerGroupsTable from "./pages/tables/groups-table";
import CommunityDashboard from "./pages/community-dashboard/CommunityDashboard";
import CommunitiesTable from "./pages/tables/communities-table";
import HoursReportsTable from "./pages/tables/hours-report-table";
import UsersTables from "./pages/tables/users-table/UsersTables";
import VolunteersTable from "./pages/tables/users-table/volunteers/VolunteersTable";
import { VolunteersLayout } from "./pages/volunteers-layout/VolunteersLayout";
import CreateLinks from "./pages/create-links/CreateLinks";
import OpeningHubSettingsPage from "./pages/opening-hub-settings/OpeningHubSettingsPage";
import CategoryVisibilityPage from "./pages/category-visibility/CategoryVisibilityPage";
import PartnerOrganizationsTable from "./pages/tables/partner-organizations-table";
import VolunteerOrganizationsPage from "./pages/tables/volunteer-organizations-table";
import { APIProvider } from "@vis.gl/react-google-maps";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
  GOOGLE_MAPS_STATISTICS_LIBRARIES,
} from "./constants/google-maps-browser";

/** לא טוענים בהפעלה - מפצל צ'אנק ונמנע ממעגל/סדר טעינה כבד (מפות / entities). */
const OpportunitiesTable = lazy(() => import("./pages/tables/opportunities-table"));
const CallCenterCoordinatorDesk = lazy(() =>
  import("./pages/call-center/CallCenterCoordinatorDesk").then((module) => ({
    default: module.CallCenterCoordinatorDesk,
  })),
);

const opportunitiesTableFallback = (
  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
    <CircularProgress />
  </Box>
);

export function useAppRoutes() {
  return createBrowserRouter([
    {
      path: "/",
      element: wrap(
        [UserProvider, {}],
        DialogProvider,
        AlertProvider,
        OrganizationProvider,
        NotificationProvider,
      )(
        <>
          <Outlet />
          <GeneralAlert isGeneral />
        </>,
      ),
      children: [
        {
          path: "*",
          element: <NotFound />,
        },
        {
          path: "/login",
          element: <Auth />,
        },
        {
          path: "/offline",
          element: <Offline />,
        },
        {
          path: "/",
          element: (
            <WrapPrivateRoute
              role={Roles.AdminsAndManager}
              element={wrap(
                RequestProvider,
                AdminHandlingProvider,
              )(
                <APIProvider
                  apiKey={GOOGLE_MAPS_API_KEY}
                  libraries={GOOGLE_MAPS_LIBRARIES}
                  language={GOOGLE_MAPS_LANGUAGE}
                  region={GOOGLE_MAPS_REGION}
                >
                  <AdminLayout />
                </APIProvider>,
              )}
            />
          ),
          children: [
            {
              path: "/home-page",
              element: <Navigate to="/volunteers" replace />,
            },
            {
              path: "/missions",
              element: <Navigate to="/volunteers" replace />,
            },
            {
              path: "/forms",
              element: <FormsPage />,
            },
            {
              path: "/notifications",
              element: <Notifications />,
            },
            {
              path: "/volunteers",
              element: <VolunteersLayout />,
              children: [
                {
                  path: "",
                  element: <UsersTables />,
                },
                {
                  path: ":associationId",
                  element: <UsersTables />,
                },
                {
                  path: "groups/:groupId",
                  element: <VolunteersTable activeTab="volunteers" />,
                },
                {
                  path: "teams/:teamId",
                  element: <VolunteersTable activeTab="teams" />,
                },
              ],
            },
            {
              path: "/hagai",
              element: <WrapPrivateRoute element={<Hagai />} />,
            },
            {
              path: "/upload-excel",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperDuperAdmin}
                  element={<ExcelButton />}
                />
              ),
            },
            {
              path: "/create-links",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperDuperAdmin}
                  element={<CreateLinks />}
                />
              ),
            },
            {
              path: "/opening-hub-settings",
              element: (
                <WrapPrivateRoute
                  role={Roles.AdminsAndManager}
                  element={<OpeningHubSettingsPage />}
                />
              ),
            },
            {
              path: "/category-visibility",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperAdmins}
                  element={<CategoryVisibilityPage />}
                />
              ),
            },
            {
              path: "/summer-campaign-leads",
              element: (
                <Navigate to="/volunteers" replace state={{ table: "summerLeads" }} />
              ),
            },
            {
              path: "/call-center-leads",
              element: <Navigate to="/volunteers" replace />,
            },
            {
              path: "/call-center",
              element: <></>,
            },
            {
              path: "/call-center-desk",
              element: (
                <Suspense fallback={opportunitiesTableFallback}>
                  <CallCenterCoordinatorDesk />
                </Suspense>
              ),
            },
            {
              path: "/partner-organizations",
              element: (
                <WrapPrivateRoute
                  role={Roles.AdminsAndManager}
                  element={<PartnerOrganizationsTable />}
                />
              ),
            },
            {
              path: "/volunteer-organizations",
              element: (
                <WrapPrivateRoute
                  role={Roles.AdminsAndManager}
                  element={<VolunteerOrganizationsPage />}
                />
              ),
            },
            {
              path: "/volunteers-groups",
              element: <VolunteerGroupsTable />,
              children: [
                {
                  path: ":groupId",
                  element: <VolunteerGroupsTable />,
                },
              ],
            },
            {
              path: "/communities/:communityId/dashboard",
              element: <CommunityDashboard />,
            },
            {
              path: "/communities",
              element: <CommunitiesTable />,
              children: [
                {
                  path: ":communityId",
                  element: <CommunitiesTable />,
                },
              ],
            },
            {
              path: "/aid-related",
              element: <VolunteersLayout />,
              children: [
                {
                  path: "",
                  element: <AidRelatedTable />,
                },
              ],
            },
            {
              path: "/statistics",
              element: (
                <APIProvider
                  apiKey={GOOGLE_MAPS_API_KEY}
                  libraries={GOOGLE_MAPS_STATISTICS_LIBRARIES}
                  language={GOOGLE_MAPS_LANGUAGE}
                  region={GOOGLE_MAPS_REGION}
                >
                  <Statistics />
                </APIProvider>
              ),
            },
            {
              path: "/events",
              element: <VolunteersLayout />,
              children: [
                {
                  path: "",
                  element: (
                    <APIProvider
                      apiKey={GOOGLE_MAPS_API_KEY}
                      libraries={GOOGLE_MAPS_LIBRARIES}
                      language={GOOGLE_MAPS_LANGUAGE}
                      region={GOOGLE_MAPS_REGION}
                      onError={(error: unknown) =>
                        console.log("Error loading Google Maps API:", error)
                      }
                      onLoad={() =>
                        console.log("Google Maps API loaded successfully")
                      }
                    >
                      <Suspense fallback={opportunitiesTableFallback}>
                        <OpportunitiesTable />
                      </Suspense>
                    </APIProvider>
                  ),
                },
                { path: "new", element: <OrganizationRequestFullContainer /> },
              ],
            },
            {
              path: "/events/:eventId",
              element: <EventCard />,
            },
            {
              path: "/groups-calendar",
              element: <GroupsCalendar />,
            },
            {
              path: "/user-requests",
              element: <VolunteersLayout />,
              children: [
                {
                  path: "",
                  element: <AssociationRequestedTable />,
                },
              ],
            },
            {
              path: "/settings",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperAdmin}
                  element={<SettingsPage />}
                />
              ),
            },
            {
              path: "/permissions",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperAdmin}
                  element={<PermissionPage />}
                />
              ),
            },
            {
              path: "/organizations",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperDuperAdmin}
                  element={<SuperAdmin />}
                />
              ),
            },
            {
              path: "/organization",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperDuperAdmin}
                  element={<OrganizationRegister />}
                />
              ),
            },
            {
              path: "/user-hours",
              element: <HoursReportsTable />,
            },
            {
              path: "/events-reports",
              element: <EventReports />,
            },
            {
              path: "/permissions",
              element: (
                <WrapPrivateRoute
                  role={Roles.SuperAdmin}
                  element={<PermissionPage />}
                />
              ),
            },
          ],
        },
      ],
    },
  ]);
}
